import { useEffect, useState } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  getEntertainmentRvcs,
  getEntertainmentMenuItems,
} from "../../../utils/ApiCalls/BackOffice/Entertainment";

// Stores { rvc_id, itemizer_id, menu_id, rvc_name, item_name, gs_outlet_id, price }.
// The 4 non-id fields are a write-time audit snapshot only - display/price
// should always be resolved live from the 3 ids wherever this is read back.
const RevenueCenterField = ({ value, onChange, vesselId }) => {
  const [rvcOptions, setRvcOptions] = useState([]);
  const [menuOptions, setMenuOptions] = useState([]);
  const [rvcId, setRvcId] = useState(value?.rvc_id || "");
  const [menuId, setMenuId] = useState(value?.menu_id || "");

  // only re-sync from the parent-held value when it actually carries a
  // linked rvc - our own onChange(undefined) call (on RVC change, before a
  // new menu item is picked) echoes back down as this same `value` prop, and
  // must NOT wipe the rvcId the user just selected
  useEffect(() => {
    if (value?.rvc_id) {
      setRvcId(value.rvc_id);
      setMenuId(value.menu_id || "");
    }
  }, [value]);

  useEffect(() => {
    if (!vesselId) return;
    const loadRvcs = async () => {
      const res = await getEntertainmentRvcs({ vesselId, limit: 1000 });
      if (!res?.error) setRvcOptions(res.data || []);
    };
    loadRvcs();
  }, [vesselId]);

  useEffect(() => {
    if (!rvcId) {
      setMenuOptions([]);
      return;
    }
    const loadMenuItems = async () => {
      const res = await getEntertainmentMenuItems({ rvcId, limit: 1000 });
      if (res?.error) return;
      // this endpoint intentionally ignores isActive/isDeleted, so filter here
      const active = (res.data || []).filter(
        (item) => item.isActive === 1 && item.isDeleted === 1
      );
      setMenuOptions(active);
    };
    loadMenuItems();
  }, [rvcId]);

  const handleRvcChange = (newRvcId) => {
    setRvcId(newRvcId);
    setMenuId("");
    onChange(undefined);
  };

  const handleMenuChange = (newMenuId) => {
    setMenuId(newMenuId);
    const rvc = rvcOptions.find((r) => r.id === rvcId);
    const menu = menuOptions.find((m) => m.id === newMenuId);
    onChange({
      rvc_id: rvcId,
      itemizer_id: menu?.itemizerId,
      menu_id: newMenuId,
      rvc_name: rvc?.name || "",
      item_name: menu?.name || "",
      gs_outlet_id: menu?.gsOutletId || "",
      price: menu?.price ?? 0,
    });
  };

  return (
    <div className="row">
      <div className="col-lg-6 col-md-12 col-sm-12 col-12">
        <div className="fieldrow">
          <label className="f_label">Revenue Center</label>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={rvcId}
            onChange={(e) => handleRvcChange(e.target.value)}
          >
            <MenuItem value="" disabled>
              Select revenue center
            </MenuItem>
            {rvcOptions.map((rvc) => (
              <MenuItem key={rvc.id} value={rvc.id}>
                {rvc.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>

      <div className="col-lg-6 col-md-12 col-sm-12 col-12">
        <div className="fieldrow">
          <label className="f_label">Menu Item</label>
          <Select
            size="small"
            fullWidth
            displayEmpty
            disabled={!rvcId}
            value={menuId}
            onChange={(e) => handleMenuChange(e.target.value)}
          >
            <MenuItem value="" disabled>
              {rvcId ? "Select menu item" : "Select a revenue center first"}
            </MenuItem>
            {menuOptions.map((menu) => (
              <MenuItem key={menu.id} value={menu.id}>
                {menu.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
};

export default RevenueCenterField;
